import {
  DiscountConfig,
  EditPricingBaselineLine,
  MarkupConfig,
  OrderChargeDraft,
  PaymentConfig,
  PaymentEntry,
  SalesModalState,
  SelectedProduct,
  SelectedSeller,
  SalesOrder,
} from '../../types/sales.types';
import { roundMoney } from '../money';
import { getCommercialSaleDate } from './commercial-sale-date';
import { canEditSalesOrderPricing } from './sales-order-actions';

const initialDiscountConfig: DiscountConfig = {
  enabled: false,
  scope: 'PER_ITEM',
  allowIndividualDiscounts: false,
  orderDiscountType: 'AMOUNT',
  orderDiscountValue: 0,
};

const initialMarkupConfig: MarkupConfig = {
  enabled: false,
  scope: 'PER_ITEM',
  allowIndividualMarkups: false,
  orderMarkupType: 'AMOUNT',
  orderMarkupValue: 0,
};

function mapSeller(order: SalesOrder): SelectedSeller | null {
  const createdBy = order.createdBy;
  const id = createdBy?.externalId;
  if (id == null || id === '') {
    return null;
  }
  return {
    id,
    firstName: createdBy.firstName ?? '',
    lastName: createdBy.lastName ?? '',
    email: createdBy.email ?? '',
  };
}

function mapProducts(order: SalesOrder): SelectedProduct[] {
  return (order.items ?? []).map((item) => ({
    salesOrderItemId: item.id,
    warehouseInventoryProduct: item.warehouseInventoryProduct,
    originalPrice: Number(item.originalPrice),
    markupType: (item.markupType ?? 'NONE') as SelectedProduct['markupType'],
    markupValue: Number(item.markupValue ?? 0),
    adjustedPrice: Number(item.adjustedPrice ?? item.originalPrice),
    discountType: (item.discountType ?? 'NONE') as SelectedProduct['discountType'],
    discountValue: Number(item.discountValue ?? 0),
    finalPrice: Number(item.finalPrice),
    isDelivered: Boolean(item.isDelivered),
  }));
}

function mapCharges(order: SalesOrder): OrderChargeDraft[] {
  return (order.charges ?? []).map((charge) => ({
    id: String(charge.id),
    categoryCode: charge.categoryCode,
    amount: Number(charge.amount),
    description: charge.description ?? undefined,
  }));
}

function mapPersistedPayments(order: SalesOrder): PaymentEntry[] {
  return (order.payments ?? [])
    .filter((payment) => payment.status === 'COMPLETED')
    .map((payment) => ({
      id: `persisted-${payment.id}`,
      method: payment.paymentMethod,
      amount: Number(payment.paymentAmount),
      cashReceived:
        payment.paymentMethod === 'CASH'
          ? Number(payment.paymentAmount) + Number(payment.changeGiven ?? 0)
          : undefined,
      bankReference: payment.bankReference ?? undefined,
      qrTransactionId: payment.qrTransactionId ?? undefined,
      paymentNotes: payment.paymentNotes ?? undefined,
      persisted: true,
    }));
}

function amountPaidFromOrder(order: SalesOrder): number {
  return roundMoney(
    (order.payments ?? [])
      .filter((payment) => payment.status === 'COMPLETED')
      .reduce((sum, payment) => sum + Number(payment.paymentAmount), 0),
  );
}

function snapshotEditPricingBaseline(products: SelectedProduct[]): EditPricingBaselineLine[] {
  return products.map((product) => ({
    warehouseInventoryProductId: product.warehouseInventoryProduct.id,
    originalPrice: product.originalPrice,
    markupType: product.markupType,
    markupValue: product.markupValue,
    adjustedPrice: product.adjustedPrice,
    discountType: product.discountType,
    discountValue: product.discountValue,
    finalPrice: product.finalPrice,
  }));
}

function sameNumericValues(values: number[]): boolean {
  if (values.length === 0) return false;
  const first = roundMoney(values[0]);
  return values.every((value) => roundMoney(value) === first);
}

function isAdjustableLine(product: SelectedProduct): boolean {
  return roundMoney(product.originalPrice) > 0;
}

function hasAppliedMarkup(product: SelectedProduct): boolean {
  return product.markupType !== 'NONE' && product.markupValue > 0;
}

function hasAppliedDiscount(product: SelectedProduct): boolean {
  return product.discountType !== 'NONE' && product.discountValue > 0;
}

/**
 * Rebuild the commercial toolbar from saved lines so edit looks like
 * nueva venta after "Aplicar a piezas".
 */
function buildMarkupConfigFromProducts(products: SelectedProduct[]): MarkupConfig {
  const marked = products.filter(hasAppliedMarkup);
  if (marked.length === 0) {
    return { ...initialMarkupConfig };
  }

  const adjustable = products.filter(isAdjustableLine);
  const allPercentage = marked.every((product) => product.markupType === 'PERCENTAGE');
  const allAmount = marked.every((product) => product.markupType === 'AMOUNT');
  const coversEveryAdjustable = adjustable.length > 0 && adjustable.every(hasAppliedMarkup);
  const uniformPercentage =
    allPercentage && sameNumericValues(marked.map((product) => product.markupValue));

  if (uniformPercentage && coversEveryAdjustable) {
    return {
      enabled: true,
      scope: 'PER_ITEM',
      allowIndividualMarkups: false,
      orderMarkupType: 'PERCENTAGE',
      orderMarkupValue: marked[0].markupValue,
    };
  }

  if (allAmount && coversEveryAdjustable) {
    const orderMarkupValue = roundMoney(
      marked.reduce(
        (sum, product) => sum + roundMoney(product.adjustedPrice - product.originalPrice),
        0,
      ),
    );
    return {
      enabled: true,
      scope: 'PER_ORDER',
      allowIndividualMarkups: false,
      orderMarkupType: 'AMOUNT',
      orderMarkupValue,
    };
  }

  return {
    enabled: true,
    scope: 'PER_ITEM',
    allowIndividualMarkups: true,
    orderMarkupType: allPercentage ? 'PERCENTAGE' : 'AMOUNT',
    orderMarkupValue: 0,
  };
}

function buildDiscountConfigFromProducts(
  order: SalesOrder,
  products: SelectedProduct[],
): DiscountConfig {
  const discounted = products.filter(hasAppliedDiscount);
  if (discounted.length === 0 && !order.hasDiscount) {
    return { ...initialDiscountConfig };
  }
  if (discounted.length === 0) {
    return { ...initialDiscountConfig };
  }

  const adjustable = products.filter(isAdjustableLine);
  const allPercentage = discounted.every((product) => product.discountType === 'PERCENTAGE');
  const allAmount = discounted.every((product) => product.discountType === 'AMOUNT');
  const coversEveryAdjustable = adjustable.length > 0 && adjustable.every(hasAppliedDiscount);
  const uniformPercentage =
    allPercentage && sameNumericValues(discounted.map((product) => product.discountValue));

  if (uniformPercentage && coversEveryAdjustable) {
    return {
      enabled: true,
      scope: 'PER_ITEM',
      allowIndividualDiscounts: false,
      orderDiscountType: 'PERCENTAGE',
      orderDiscountValue: discounted[0].discountValue,
    };
  }

  if (allAmount && coversEveryAdjustable) {
    const orderDiscountValue = roundMoney(
      discounted.reduce(
        (sum, product) => sum + roundMoney(product.adjustedPrice - product.finalPrice),
        0,
      ),
    );
    return {
      enabled: true,
      scope: 'PER_ORDER',
      allowIndividualDiscounts: false,
      orderDiscountType: 'AMOUNT',
      orderDiscountValue,
    };
  }

  return {
    enabled: true,
    scope: order.discountScope ?? 'PER_ITEM',
    allowIndividualDiscounts: true,
    orderDiscountType: allPercentage ? 'PERCENTAGE' : 'AMOUNT',
    orderDiscountValue: 0,
  };
}

export function applyEditPricingBaseline(
  products: SelectedProduct[],
  baseline: EditPricingBaselineLine[] | null,
): SelectedProduct[] {
  if (baseline == null || baseline.length === 0) {
    return products;
  }

  const byId = new Map(baseline.map((line) => [line.warehouseInventoryProductId, line]));
  return products.map((product) => {
    const saved = byId.get(product.warehouseInventoryProduct.id);
    if (saved == null) {
      return product;
    }
    return {
      ...product,
      originalPrice: saved.originalPrice,
      editLayerBasePrice: undefined,
      markupType: saved.markupType,
      markupValue: saved.markupValue,
      adjustedPrice: saved.adjustedPrice,
      discountType: saved.discountType,
      discountValue: saved.discountValue,
      finalPrice: saved.finalPrice,
    };
  });
}

export function hydrateSalesModalFromOrder(order: SalesOrder): SalesModalState {
  const products = mapProducts(order);
  const payments = mapPersistedPayments(order);
  const amountPaid = amountPaidFromOrder(order);
  const grandTotal = roundMoney(Number(order.totalAmount));
  const remaining = Math.max(0, roundMoney(grandTotal - amountPaid));
  const pricingEditable = canEditSalesOrderPricing(order.status, remaining);
  const currentStep = pricingEditable ? 2 : 3;

  const paymentConfig: PaymentConfig = {
    allowPartialPayment: true,
    partialPaymentScope: order.partialPaymentScope ?? 'PER_ORDER',
    payments,
    allowMultiplePayments: payments.length > 1,
    useGlobalPayment: true,
    cashAmount: 0,
    partialPaymentAmount: remaining,
  };

  const saleDate = getCommercialSaleDate({
    saleDate: order.saleDate,
    createdAt: order.createdAt,
  });

  const markupConfig = buildMarkupConfigFromProducts(products);
  const discountConfig = buildDiscountConfigFromProducts(order, products);
  const hasCommercialConfig = markupConfig.enabled || discountConfig.enabled;

  return {
    currentStep,
    step2FlowPhase: hasCommercialConfig ? 2 : 1,
    step2PaymentsLocked: false,
    selectedLocation: order.location,
    selectedProducts: products,
    selectedCustomer: order.customer ?? null,
    selectedSeller: mapSeller(order),
    // Keep saved commercial datetime; default to "keep" so customer-only saves
    // do not rewrite saleDate. User with edit_sale_date may switch to current/custom.
    saleDate,
    saleDateMode: 'keep',
    discountConfig,
    markupConfig,
    paymentConfig,
    orderCharges: mapCharges(order),
    hasInvoice: Boolean(order.hasInvoice),
    orderSummary: null,
    mode: 'edit',
    editingOrderId: order.id,
    editingOrderCode: order.orderCode,
    originalGrandTotal: grandTotal,
    originalAmountPaid: amountPaid,
    pricingEditable,
    editPricingBaseline: snapshotEditPricingBaseline(products),
    editAdjustmentLayerActive: false,
  };
}
